import { STAGE } from '../../design/lab/palette';
import React, { useRef, useEffect } from 'react';
import { gsap, useReducedMotion } from '../../animations';

interface TraceLineProps {
    text: string;
    /** A call is the agent reaching for a tool; a result is what came back. */
    kind: 'call' | 'result';
    accent: string;
    /** Last line in the run — do not draw the rail onward from here. */
    last?: boolean;
}

/**
 * One step in the agent's visible timeline.
 *
 * xAI's write-up rejected the standard three animated dots because it was "too
 * little information, making it hard for users to tell whether the Bot was
 * working or stuck". A step timeline fixes that, but only if steps visibly
 * *land* — a line that simply appears reads as static text. So the rail draws
 * downward, the node pops, and the label slides in behind it. The motion is the
 * evidence that something happened.
 */
const TraceLine: React.FC<TraceLineProps> = ({ text, kind, accent, last }) => {
    const rowRef = useRef<HTMLDivElement>(null);
    const railRef = useRef<HTMLSpanElement>(null);
    const nodeRef = useRef<HTMLSpanElement>(null);
    const labelRef = useRef<HTMLSpanElement>(null);
    const reduced = useReducedMotion();

    useEffect(() => {
        if (reduced) return;
        const ctx = gsap.context(() => {
            const tl = gsap.timeline();
            tl.fromTo(railRef.current,
                { scaleY: 0 },
                { scaleY: 1, duration: 0.22, ease: 'power2.out', transformOrigin: 'top' });
            tl.fromTo(nodeRef.current,
                { scale: 0 },
                { scale: 1, duration: 0.42, ease: 'elastic.out(1, 0.5)' }, 0.1);
            tl.fromTo(labelRef.current,
                { x: -8, opacity: 0 },
                { x: 0, opacity: 1, duration: 0.3, ease: 'power3.out' }, 0.12);
        }, rowRef);
        return () => ctx.revert();
    }, [reduced]);

    const isCall = kind === 'call';

    return (
        <div
            ref={rowRef}
            style={{
                display: 'grid',
                gridTemplateColumns: '16px 1fr',
                columnGap: '9px',
                alignItems: 'start',
                paddingLeft: '14px',
                minHeight: isCall ? '22px' : '20px',
            }}
        >
            {/* rail + node */}
            <span style={{ position: 'relative', display: 'block', height: '100%', minHeight: '20px' }}>
                <span
                    ref={railRef}
                    aria-hidden
                    style={{
                        position: 'absolute', left: '7px', top: '11px',
                        width: '1px', height: last ? '0' : 'calc(100% - 4px)',
                        background: `linear-gradient(${accent}66, ${accent}18)`,
                        willChange: 'transform',
                    }}
                />
                <span
                    ref={nodeRef}
                    aria-hidden
                    style={{
                        position: 'absolute', left: isCall ? '4px' : '5px', top: isCall ? '5px' : '6px',
                        width: isCall ? '7px' : '5px',
                        height: isCall ? '7px' : '5px',
                        borderRadius: '50%',
                        background: isCall ? accent : 'transparent',
                        border: isCall ? 'none' : `1px solid ${accent}aa`,
                        boxShadow: isCall ? `0 0 8px ${accent}88` : 'none',
                        willChange: 'transform',
                    }}
                />
            </span>

            <span
                ref={labelRef}
                style={{
                    fontFamily: "'JetBrains Mono','Fira Code',monospace",
                    fontSize: '11.5px',
                    lineHeight: 1.7,
                    color: isCall ? accent : STAGE.muted,
                    wordBreak: 'break-word',
                    willChange: 'transform, opacity',
                }}
            >
                {text}
            </span>
        </div>
    );
};

export default TraceLine;
